import { useId } from "react";

export default function GenderSelfDescribeIconMini({
  size = 28,
  active = false,
  title = "Self describe",
  ...rest
}) {
  const uid = useId();
  const id = (name) => `${uid}-${name}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={title}
      style={{ display: "block" }}
      data-preserve
      {...rest}
    >
      <title>{title}</title>

      <defs>
        {/* Outer glow – neutral rainbow-ish aura */}
        <radialGradient id={id("rimGlow")} cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="#050814" stopOpacity="0" />
          <stop offset="80%" stopColor="#FF9FD6" stopOpacity="0.55" />
          <stop offset="95%" stopColor="#7F8CFF" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#4BFFC9" stopOpacity="0.75" />
        </radialGradient>

        {/* Rim stroke gradient – pink → violet → teal */}
        <linearGradient
          id={id("rimStroke")}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#FF9FD6" />
          <stop offset="40%" stopColor="#B476FF" />
          <stop offset="100%" stopColor="#4BFFC9" />
        </linearGradient>

        {/* Inner face fill */}
        <radialGradient id={id("faceFill")} cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#151524" />
          <stop offset="70%" stopColor="#070711" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>

        {/* Soft upper highlight */}
        <radialGradient id={id("rimHighlight")} cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#ffffff" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>

        {/* Symbol stroke – same palette */}
        <linearGradient
          id={id("symbolStroke")}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#FF9FD6" />
          <stop offset="40%" stopColor="#B476FF" />
          <stop offset="100%" stopColor="#4BFFC9" />
        </linearGradient>

        {/* Glow for the symbol */}
        <filter
          id={id("symbolGlow")}
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="b1" />
          <feColorMatrix
            in="b1"
            type="matrix"
            values="
              1 0 0 0 0.9
              0 1 0 0 0.7
              0 0 1 0 0.9
              0 0 0 1 0
            "
          />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Active aura */}
        <filter
          id={id("activeAura")}
          x="-80%"
          y="-80%"
          width="260%"
          height="260%"
        >
          <feGaussianBlur stdDeviation="6" result="b1" />
          <feMerge>
            <feMergeNode in="b1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Tiny spark glows */}
        <filter
          id={id("spark")}
          x="-200%"
          y="-200%"
          width="400%"
          height="400%"
        >
          <feGaussianBlur stdDeviation="0.6" />
        </filter>
      </defs>

      {/* ===== Badge base ===== */}
      <g filter={active ? `url(#${id("activeAura")})` : undefined}>
        <circle cx="32" cy="32" r="30" fill={`url(#${id("rimGlow")})`} />
        <circle
          cx="32"
          cy="32"
          r="30"
          fill="none"
          stroke={`url(#${id("rimStroke")})`}
          strokeWidth="4"
        />
        <circle
          cx="32"
          cy="24"
          r="26"
          fill={`url(#${id("rimHighlight")})`}
          opacity="0.4"
        />
        <circle cx="32" cy="32" r="24" fill={`url(#${id("faceFill")})`} />
        <circle
          cx="32"
          cy="32"
          r="24"
          fill="none"
          stroke="#ffffff"
          strokeOpacity="0.16"
          strokeWidth="1.25"
        />
      </g>

      {/* ===== Self-describe symbol: question mark + dot + sparkle ===== */}
      <g
        filter={`url(#${id("symbolGlow")})`}
        stroke={`url(#${id("symbolStroke")})`}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {/* Question mark curve */}
        <path
          d="
            M 26 28
            C 26 24, 30 22, 33 22
            C 36 22, 38.5 24, 38.5 27
            C 38.5 29.2, 37.3 30.7, 35.8 31.7
            C 34.3 32.7, 33 33.4, 33 35.2
          "
        />

        {/* Question mark dot */}
        <circle cx="33" cy="38.5" r="1.6" />

        {/* Small sparkle/star to the side – hinting “your own story” */}
        <path
          d="
            M 23 24
            L 24.4 25.2
            L 26 24
            L 24.4 22.8
            Z
          "
          strokeWidth="2.4"
        />
      </g>

      {/* ===== Twinkling sparks ===== */}
      <g opacity="0.9">
        <circle
          cx="20"
          cy="22"
          r="0.8"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="44"
          cy="18"
          r="0.6"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="48"
          cy="36"
          r="0.7"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
        <circle
          cx="18"
          cy="40"
          r="0.5"
          fill="#FFFFFF"
          filter={`url(#${id("spark")})`}
        />
      </g>
    </svg>
  );
}
